/**
 * Live W8 board mutations. Every write carries operator_reason (≥8).
 * Paths fail closed via safeBoardPath when contract.generated lags board gates.
 */
import { path, api } from "./api.js";
import { explainBoardError, seatIdFromSoftSeat, toServerRole } from "./board-seat-adapter.js";

/** Resolve board path — never throw Unknown operation_id into React. */
export function safeBoardPath(op, opts) {
  try {
    return path(op, opts);
  } catch (_e) {
    const pp = (opts && opts.pathParams) || [];
    const enc = (x) => encodeURIComponent(String(x ?? ""));
    const did = enc(pp[0]);
    const s1 = enc(pp[1]);
    if (op === "get__devices_p_pins") return "/devices/" + did + "/pins";
    if (op === "get__devices_p_ports") return "/devices/" + did + "/ports";
    if (op === "get__devices_p_board-telemetry") return "/devices/" + did + "/board-telemetry";
    if (op === "get__devices_p") return "/devices/" + did;
    if (op === "get__devices_p_board-loop") return "/devices/" + did + "/board-loop";
    if (op === "get__devices_p_connections") return "/devices/" + did + "/connections";
    if (op === "get__devices_p_storage_card") return "/devices/" + did + "/storage/card";
    if (op === "put__devices_p_pins_p") return "/devices/" + did + "/pins/" + s1;
    if (op === "delete__devices_p_pins_p") return "/devices/" + did + "/pins/" + s1;
    if (op === "post__devices_p_pins_p_confirm") return "/devices/" + did + "/pins/" + s1 + "/confirm";
    if (op === "post__devices_p_pins_p_learn") return "/devices/" + did + "/pins/" + s1 + "/learn";
    if (op === "post__devices_p_pins_p_override") return "/devices/" + did + "/pins/" + s1 + "/override";
    if (op === "delete__devices_p_pins_p_override") return "/devices/" + did + "/pins/" + s1 + "/override";
    if (op === "post__devices_p_connections_p_dismiss") {
      return "/devices/" + did + "/connections/" + s1 + "/dismiss";
    }
    if (op === "post__devices_p_storage_card_mount") return "/devices/" + did + "/storage/card/mount";
    if (op === "post__devices_p_storage_card_unmount") {
      return "/devices/" + did + "/storage/card/unmount";
    }
    if (op === "post__devices_p_storage_card_format") {
      return "/devices/" + did + "/storage/card/format";
    }
    if (op === "post__devices_p_board_ask") return "/devices/" + did + "/board/ask";
    return "";
  }
}

async function call(url, opts) {
  if (!url) {
    return { ok: false, error: "Board route is not available for this client build." };
  }
  try {
    return { ok: true, data: await api(url, opts || {}) };
  } catch (e) {
    const msg = String((e && e.message) || e);
    return { ok: false, error: explainBoardError(msg), raw: msg };
  }
}

export function bindSeat(deviceId, seatId, { role, function_label = "", operator_reason }) {
  const serverRole = toServerRole(role);
  if (!serverRole) {
    return Promise.resolve({
      ok: false,
      error: "Role is not a W8 assignable role (Soft bus chips cannot bind).",
    });
  }
  if (String(operator_reason || "").trim().length < 8) {
    return Promise.resolve({ ok: false, error: "operator_reason must be at least 8 characters." });
  }
  return call(safeBoardPath("put__devices_p_pins_p", { pathParams: [deviceId, seatId] }), {
    method: "PUT",
    body: {
      role: serverRole,
      function_label: function_label || "",
      operator_reason: String(operator_reason).trim(),
    },
  });
}

export function confirmSeat(deviceId, seatId, { expected_version, operator_reason }) {
  if (String(operator_reason || "").trim().length < 8) {
    return Promise.resolve({ ok: false, error: "operator_reason must be at least 8 characters." });
  }
  return call(safeBoardPath("post__devices_p_pins_p_confirm", { pathParams: [deviceId, seatId] }), {
    method: "POST",
    body: {
      expected_version: Number(expected_version) || 1,
      operator_reason: String(operator_reason).trim(),
    },
  });
}

export function releaseSeat(deviceId, seatId) {
  return call(safeBoardPath("delete__devices_p_pins_p", { pathParams: [deviceId, seatId] }), {
    method: "DELETE",
  });
}

export function learnSeat(deviceId, seatId, { window_s = 20, expect_rise = false } = {}) {
  return call(safeBoardPath("post__devices_p_pins_p_learn", { pathParams: [deviceId, seatId] }), {
    method: "POST",
    body: { window_s, expect_rise },
  });
}

export function mountCard(deviceId, operator_reason) {
  return call(safeBoardPath("post__devices_p_storage_card_mount", { pathParams: [deviceId] }), {
    method: "POST",
    body: { operator_reason: String(operator_reason || "").trim() },
  });
}

export function unmountCard(deviceId, operator_reason) {
  return call(safeBoardPath("post__devices_p_storage_card_unmount", { pathParams: [deviceId] }), {
    method: "POST",
    body: { operator_reason: String(operator_reason || "").trim() },
  });
}

export function formatCard(deviceId, operator_reason) {
  const reason = String(operator_reason || "").trim();
  if (reason.length < 8) {
    return Promise.resolve({ ok: false, error: "operator_reason must be at least 8 characters." });
  }
  return call(safeBoardPath("post__devices_p_storage_card_format", { pathParams: [deviceId] }), {
    method: "POST",
    body: { operator_reason: reason, confirm: true },
  });
}

export function overrideSeat(deviceId, seatId, { forced_state, ttl_seconds = 900, operator_reason }) {
  if (String(operator_reason || "").trim().length < 8) {
    return Promise.resolve({ ok: false, error: "operator_reason must be at least 8 characters." });
  }
  const forced = String(forced_state || "").trim().toLowerCase();
  if (!["hold_low", "hold_high", "isolate"].includes(forced)) {
    return Promise.resolve({ ok: false, error: "forced_state must be hold_low | hold_high | isolate." });
  }
  return call(safeBoardPath("post__devices_p_pins_p_override", { pathParams: [deviceId, seatId] }), {
    method: "POST",
    body: {
      forced_state: forced,
      ttl_seconds: Number(ttl_seconds) || 900,
      operator_reason: String(operator_reason).trim(),
    },
  });
}

export function clearOverride(deviceId, seatId) {
  return call(safeBoardPath("delete__devices_p_pins_p_override", { pathParams: [deviceId, seatId] }), {
    method: "DELETE",
  });
}

export function dismissConnection(deviceId, gpio, { reason, dismissed = true } = {}) {
  if (dismissed && String(reason || "").trim().length < 1) {
    return Promise.resolve({ ok: false, error: "dismiss_reason_required" });
  }
  return call(
    safeBoardPath("post__devices_p_connections_p_dismiss", { pathParams: [deviceId, gpio] }),
    {
      method: "POST",
      body: { dismissed: !!dismissed, reason: String(reason || "").trim() },
    },
  );
}

export function seatIdOfPin(pin) {
  if (pin && pin.seat_id) return String(pin.seat_id);
  return seatIdFromSoftSeat(pin && pin.seat);
}

/** Live grounded Ask — POST …/board/ask. */
export function askBoard(deviceId, { question, seat_id = null, focus = null } = {}) {
  const q = String(question || "").trim();
  if (q.length < 3) {
    return Promise.resolve({ ok: false, error: "Question must be at least 3 characters." });
  }
  const body = { question: q };
  if (seat_id) body.seat_id = String(seat_id).trim();
  if (focus) body.focus = String(focus).trim();
  return call(safeBoardPath("post__devices_p_board_ask", { pathParams: [deviceId] }), {
    method: "POST",
    body,
  });
}

/** Poll board-telemetry until learn terminates (GPIO never comes from learn HTTP). */
export async function pollLearnResult(deviceId, { timeoutMs = 25000, everyMs = 1500 } = {}) {
  const started = Date.now();
  let last = null;
  const teleUrl = safeBoardPath("get__devices_p_board-telemetry", { pathParams: [deviceId] });
  if (!teleUrl) {
    return { ok: false, error: "Board telemetry route is not available for this client build." };
  }
  while (Date.now() - started < timeoutMs) {
    try {
      last = await api(teleUrl);
      const learn = (last && last.telemetry && last.telemetry.learn) || (last && last.learn) || null;
      if (learn && learn.state && !/running|pending|in_progress/i.test(String(learn.state))) {
        return { ok: true, learn, telemetry: last };
      }
    } catch (e) {
      return { ok: false, error: explainBoardError((e && e.message) || e) };
    }
    await new Promise((r) => setTimeout(r, everyMs));
  }
  return { ok: false, error: "Learn window timed out with no terminal result", telemetry: last };
}
